// What a cash-on-delivery client is told at the checkout, BEFORE she orders.
//
// ─── 2026-09-29, THE OWNER'S « 1a » ─────────────────────────────────────────
//
// Every client pays at least an advance, cash on delivery included
// (worker/src/lib/orderMoney.ts). The checkout still sold « Paiement à la
// livraison » as « Payez en espèces à la réception de votre album » — and the
// payment request then asked her for 100 MAD by transfer. The option now says
// it first: a small advance by transfer before printing, the rest in cash at
// delivery.
//
// The figure is the worker's (GET /settings/advance — the saved « RIB
// paiment » reply's figure first, app_settings second), capped at the page's
// own total exactly like the payment request (`advanceSplit` mirrors
// worker/src/lib/paymentRequest.ts `paymentTerms`, held to it by
// worker/src/lib/clientAdvance.test.ts). No figure → « une petite avance »,
// never a number typed into this file.
//
// ⚠️ NO `@/` IMPORTS HERE: the worker's parity test imports this file, and the
// worker's compiler has no `@` alias. `TFunc` is lib/i18n.tsx's shape, spelled
// out.
type TFunc = (fr: string, vars?: Record<string, string | number>) => string;

export interface AdvanceSplit {
  /** The order total the page shows. */
  total: number;
  /** Paid by transfer before printing — never more than the total. */
  advance: number;
  /** Paid in cash to the courier. */
  rest: number;
}

/** Mirror of the worker's `paymentTerms`: null when either figure is unusable. */
export function advanceSplit(
  total: number | string | null | undefined,
  minAdvance: number | string | null | undefined,
): AdvanceSplit | null {
  const t = Number(total);
  if (!Number.isFinite(t) || t <= 0) return null;
  const raw = Number(minAdvance);
  const adv = Number.isFinite(raw) && raw > 0 ? raw : 0;
  if (adv <= 0) return null;
  const advance = Math.min(adv, t);
  return { total: t, advance, rest: Math.max(0, t - advance) };
}

/** 299 → « 299 », 323.5 → « 323.5 » — as the page's total prints it. */
function mad(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Math.round(n * 100) / 100);
}

/** The cash-on-delivery option's two lines, in the admin's language (French for every client). */
export function codOptionCopy(t: TFunc, split: AdvanceSplit | null): { title: string; subtitle: string } {
  const title = t('Avance + paiement à la livraison 📦');
  if (!split) {
    return {
      title,
      subtitle: t('Une petite avance par virement pour lancer l’impression, puis le reste en espèces à la réception de votre album.'),
    };
  }
  if (split.rest <= 0) {
    return {
      title,
      subtitle: t('Une petite avance de {advance} MAD par virement pour lancer l’impression.', { advance: mad(split.advance) }),
    };
  }
  return {
    title,
    subtitle: t(
      'Une petite avance de {advance} MAD par virement pour lancer l’impression, puis {rest} MAD en espèces à la réception de votre album.',
      { advance: mad(split.advance), rest: mad(split.rest) },
    ),
  };
}
